<script lang="ts">
	import type { SvelteAsr } from 'types/common'
	import type { Column, SortDirection } from '@isoftdata/svelte-table'
	import type { SettingValueStore } from 'stores/setting-value'

	import { History, type PrintJob } from '@isoftdata/svelte-report-viewer'
	import { graphql } from '$houdini'
	import { booleanToString } from '@isoftdata/utility-boolean'

	interface Props {
		history?: Array<PrintJob>
		asr: SvelteAsr
		currentPageNumber: number
		sortColumnProperty: string
		sortDirection: 'ASC' | 'DESC'
		totalItemsCount: number
		showCancelledJobs: SettingValueStore<boolean>
		showSentJobs: SettingValueStore<boolean>
		showReadyJobs: SettingValueStore<boolean>
		showFailedJobs: SettingValueStore<boolean>
		showDelayedJobsOnly: SettingValueStore<boolean>
		perPageCount: number
	}

	let {
		history = [],
		asr,
		currentPageNumber,
		sortColumnProperty,
		sortDirection,
		totalItemsCount,
		showCancelledJobs,
		showSentJobs,
		showReadyJobs,
		showFailedJobs,
		showDelayedJobsOnly,
		perPageCount,
	}: Props = $props()

	const setPrintJobStatus = graphql(`
		mutation SetPrintJobStatus($input: [SetPrintJobStatusInput!]!) {
			setPrintJobStatus(input: $input)
		}
	`)

	function onPreviewClick(printJob: PrintJob) {
		localStorage.setItem('reportViewerPreviewPrintJob', JSON.stringify(printJob))
		asr.go('app.report-viewer.reports.preview', { selectedReportId: printJob.reportId, preview: true })
	}

	function onSetReadyClick(printJob: PrintJob) {
		setPrintJobStatus.mutate({
			input: [
				{
					id: printJob.id,
					status: 'READY',
				},
			],
		})
	}

	function onCancelClick(printJobIds: Array<number>) {
		setPrintJobStatus.mutate({
			input: printJobIds.map(id => ({
				id,
				status: 'CANCELLED',
			})),
		})
	}

	function filterChange(
		val: boolean,
		parameter: 'showCancelledJobs' | 'showSentJobs' | 'showReadyJobs' | 'showFailedJobs' | 'showDelayedJobsOnly',
	) {
		asr.go(null, { [parameter]: booleanToString(val) }, { inherit: true })
	}

	function onPageChange(context: { pageNumber: number }) {
		asr.go(null, { pageNumber: context.pageNumber }, { inherit: true })
	}

	async function columnClickedMethod(clickedColumn: Column<PrintJob>, sortDirection: SortDirection) {
		asr.go(null, { sortColumnProperty: clickedColumn.property, sortDirection, pageNumber: 1 }, { inherit: true })
	}
</script>

<History
	showDelayUntil
	showAdditionalStatusFilters
	{history}
	{currentPageNumber}
	{perPageCount}
	{sortColumnProperty}
	{sortDirection}
	{totalItemsCount}
	{columnClickedMethod}
	refreshHref={asr.makePath(null, { lastRefreshTime: Date.now(), pageNumber: 1 }, { inherit: true })}
	bind:showCancelledJobs={$showCancelledJobs}
	bind:showSentJobs={$showSentJobs}
	bind:showReadyJobs={$showReadyJobs}
	bind:showFailedJobs={$showFailedJobs}
	bind:showDelayedJobsOnly={$showDelayedJobsOnly}
	previewClick={onPreviewClick}
	setReadyClick={onSetReadyClick}
	cancelClick={onCancelClick}
	showCancelledChange={e => filterChange(e, 'showCancelledJobs')}
	showSentChange={e => filterChange(e, 'showSentJobs')}
	showReadyChange={e => filterChange(e, 'showReadyJobs')}
	showFailedChange={e => filterChange(e, 'showFailedJobs')}
	showDelayedOnlyChange={e => filterChange(e, 'showDelayedJobsOnly')}
	pageChange={onPageChange}
></History>
